import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Switch,
  FormGroup,
  Avatar,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search,
  Refresh,
  ExpandMore,
  Save,
  Clear,
  Extension as ExtensionIcon,
  Apartment as ApartmentIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  CancelOutlined as CancelOutlinedIcon,
  Tune as TuneIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';

import { LoadRoles, getComponentPermissions, updateComponentPermissions, getDepartmentComponents, getAccessibleDepartments } from '../BaseURL';
import { getAllDepartments } from '../data/department';
import axios from 'axios';
import Swal from 'sweetalert2';

const ComponentAccess = () => {
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [departmentComponents, setDepartmentComponents] = useState({});
  const [componentPermissions, setComponentPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDepartments, setExpandedDepartments] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  // 'all' | 'granted' | 'denied' — filters the per-department component lists.
  const [accessFilter, setAccessFilter] = useState('all');

  const PRIMARY = '#1A9B7F';
  const PRIMARY_DARK = '#157763';

  const selectedRoleObj = useMemo(
    () => roles.find((r) => String(r.id) === String(selectedRole)) || null,
    [roles, selectedRole]
  );

  // Load roles on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  // Load component permissions when role is selected
  useEffect(() => {
    if (selectedRole) {
      loadComponentPermissions();
    }
  }, [selectedRole]);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoles(response.data.results);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };



  const loadComponentPermissions = async () => {
    if (!selectedRole) return;

    console.log('Loading component permissions for role:', selectedRole);

    try {
      setLoading(true);
      
      // Step 1: Get accessible departments for this role from accessibledepartments table
      console.log('Step 1: Fetching accessible departments for role...');
      const accessibleDeptsResponse = await axios.get(getAccessibleDepartments(selectedRole));
      console.log('Accessible departments response:', accessibleDeptsResponse.data);
      
      if (!accessibleDeptsResponse.data.success) {
        throw new Error('Failed to load accessible departments');
      }
      
      const accessibleDepartmentIds = accessibleDeptsResponse.data.results.map(dept => dept.department_id);
      console.log('Accessible department IDs:', accessibleDepartmentIds);
      
      // Step 2: Get department details from local data and filter accessible ones
      console.log('Step 2: Getting department details from local data...');
      const allDepartments = getAllDepartments();
      console.log('All departments from local data:', allDepartments);
      
      // Filter departments to only show accessible ones
      const accessibleDepartments = allDepartments.filter(dept => 
        accessibleDepartmentIds.includes(dept.id)
      );
      console.log('Accessible departments:', accessibleDepartments);
      
      setDepartments(accessibleDepartments);
      
      // Step 3: Load components for each accessible department
      const componentsData = {};
      const permissionsData = {};

      for (const dept of accessibleDepartments) {
        console.log(`Step 3: Processing department: ${dept.name} (ID: ${dept.id})`);
        
        try {
          // Get all components for this department from department_components table
          console.log(`Fetching components for department ${dept.id}...`);
          const componentsResponse = await axios.get(getDepartmentComponents(dept.id));
          console.log('Components response:', componentsResponse.data);
          
          if (componentsResponse.data.success) {
            componentsData[dept.id] = componentsResponse.data.components;
            console.log(`Loaded ${componentsResponse.data.components.length} components for department ${dept.id}`);
          }

          // Get permissions for this role and department from component_permissions table
          console.log(`Fetching permissions for role ${selectedRole} and department ${dept.id}...`);
          const permissionsResponse = await axios.get(getComponentPermissions(selectedRole, dept.id));
          console.log('Permissions response:', permissionsResponse.data);
          
          if (permissionsResponse.data.success) {
            permissionsData[dept.id] = permissionsResponse.data.permissions;
            console.log(`Loaded permissions for department ${dept.id}:`, permissionsResponse.data.permissions);
            console.log(`Permissions data structure for department ${dept.id}:`, typeof permissionsResponse.data.permissions, permissionsResponse.data.permissions);
          }
        } catch (error) {
          console.error(`Error loading data for department ${dept.id}:`, error);
        }
      }

      console.log('Final components data:', componentsData);
      console.log('Final permissions data:', permissionsData);

      setDepartmentComponents(componentsData);
      setComponentPermissions(permissionsData);
    } catch (error) {
      console.error('Error loading component permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setExpandedDepartments(new Set());
    setHasChanges(false);
  };

  const handleDepartmentToggle = (departmentId) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentId)) {
        newSet.delete(departmentId);
      } else {
        newSet.add(departmentId);
      }
      return newSet;
    });
  };

  // Grant or revoke every component inside one department at once.
  const handleBulkDepartmentAccess = (departmentId, grant) => {
    const components = departmentComponents[departmentId] || [];
    if (components.length === 0) return;
    setComponentPermissions((prev) => {
      const next = { ...prev };
      if (!next[departmentId]) next[departmentId] = {};
      components.forEach((c) => {
        next[departmentId][c.component_name] = { access: !!grant };
      });
      return next;
    });
    setHasChanges(true);
  };

  const handlePermissionChange = (departmentId, componentName, checked) => {
    console.log(`Setting access for ${componentName} in department ${departmentId} to: ${checked}`);
    
    setComponentPermissions(prev => {
      const newPermissions = { ...prev };
      
      if (!newPermissions[departmentId]) {
        newPermissions[departmentId] = {};
      }
      
      newPermissions[departmentId][componentName] = {
        access: checked
      };
      
      return newPermissions;
    });
    
    setHasChanges(true);
  };

  const savePermissions = async () => {
    if (!selectedRole || !hasChanges) return;

    console.log('Saving permissions for role:', selectedRole);
    console.log('Permissions to save:', componentPermissions);

    try {
      setLoading(true);
      
      const permissionsToUpdate = [];
      
      Object.entries(componentPermissions).forEach(([departmentId, components]) => {
        Object.entries(components).forEach(([componentName, permissions]) => {
          console.log(`Preparing permission: roleId=${selectedRole}, departmentId=${departmentId}, componentName=${componentName}, permissions=`, permissions);
          permissionsToUpdate.push({
            roleId: parseInt(selectedRole),
            departmentId: parseInt(departmentId),
            componentName,
            permissions: JSON.stringify(permissions)
          });
        });
      });

      console.log('Final permissions array to send:', permissionsToUpdate);

      const response = await axios.post(updateComponentPermissions, {
        permissions: permissionsToUpdate
      });

      console.log('Save response:', response.data);

      if (response.data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Component access updated successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update component access',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetChanges = () => {
    loadComponentPermissions();
    setHasChanges(false);
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPermissionStatus = (departmentId, componentName) => {
    const permissions = componentPermissions[departmentId]?.[componentName];
    console.log(`Getting permission status for ${componentName} in department ${departmentId}:`, permissions);
    if (!permissions) return { access: false };
    return permissions;
  };

  const hasAccess = (departmentId, componentName) => {
    const permissions = getPermissionStatus(departmentId, componentName);
    const hasAccess = permissions.access === true;
    console.log(`Access check for ${componentName} in department ${departmentId}: ${hasAccess}`);
    return hasAccess;
  };

  // Aggregate counts across all departments for the stats strip.
  const stats = useMemo(() => {
    let total = 0;
    let granted = 0;
    Object.entries(departmentComponents).forEach(([deptId, comps]) => {
      (comps || []).forEach((c) => {
        total += 1;
        if (componentPermissions[deptId]?.[c.component_name]?.access) granted += 1;
      });
    });
    return { total, granted, denied: total - granted, departments: filteredDepartments.length };
  }, [departmentComponents, componentPermissions, filteredDepartments.length]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            color: 'white', boxShadow: `0 4px 12px ${alpha(PRIMARY, 0.35)}`,
          }}>
            <ExtensionIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              Component Access
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.25 }}>
              Grant or revoke per-component permissions for each role.
            </Typography>
          </Box>
        </Stack>

        {/* Save / Reset live in the header so they're always visible when needed */}
        {hasChanges && (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={resetChanges}
              startIcon={<Clear sx={{ fontSize: 18 }} />}
              disabled={loading}
              sx={{
                textTransform: 'none', fontWeight: 700, color: 'text.secondary',
                borderRadius: 1.75, px: 2,
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={savePermissions}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Save sx={{ fontSize: 18 }} />}
              disabled={loading}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 1.75, px: 2.5,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                boxShadow: `0 4px 12px ${alpha(PRIMARY, 0.32)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, #126553 100%)`,
                  boxShadow: `0 6px 16px ${alpha(PRIMARY, 0.4)}`,
                },
              }}
            >
              Save Changes
            </Button>
          </Stack>
        )}
      </Stack>

      {/* ── Role + filter controls ────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        borderRadius: 2, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', p: { xs: 1.5, md: 2 }, mb: 2,
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Role"
              disabled={rolesLoading}
              startAdornment={
                <InputAdornment position="start">
                  <AdminPanelSettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              {rolesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading roles…
                </MenuItem>
              ) : (
                roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>{role.role}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search departments…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!selectedRole}
            sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Access filter chips */}
          <Stack direction="row" spacing={0.75}>
            {[
              { key: 'all',     label: 'All',     color: 'text.secondary' },
              { key: 'granted', label: 'Granted', color: '#4ade80' },
              { key: 'denied',  label: 'Denied',  color: '#f87171' },
            ].map((f) => {
              const active = accessFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  size="small"
                  label={f.label}
                  onClick={() => setAccessFilter(f.key)}
                  disabled={!selectedRole}
                  sx={{
                    height: 30, fontWeight: 700, fontSize: '0.75rem',
                    px: 0.5,
                    bgcolor: active ? alpha(PRIMARY, 0.18) : 'background.paper',
                    color: active ? PRIMARY : f.color,
                    border: '1px solid',
                    borderColor: active ? alpha(PRIMARY, 0.40) : 'divider',
                    '&:hover': { bgcolor: alpha(PRIMARY, 0.10) },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Empty / first-load state ──────────────────────────────────────── */}
      {!selectedRole && !rolesLoading && (
        <Paper elevation={0} sx={{
          borderRadius: 2, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', textAlign: 'center', py: 8, px: 3,
        }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            bgcolor: alpha(PRIMARY, 0.12),
            border: `1px solid ${alpha(PRIMARY, 0.30)}`,
            color: PRIMARY,
            mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LockIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
            Pick a role to manage its components
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
            Choose a role from the dropdown above. You'll be able to toggle access to every component the system exposes.
          </Typography>
        </Paper>
      )}

      {/* ── Stats strip + content ─────────────────────────────────────────── */}
      {selectedRole && (
        <>
          <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" useFlexGap>
            {[
              { label: 'Selected Role',  value: selectedRoleObj?.role || '—', icon: <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />, color: PRIMARY,    isText: true },
              { label: 'Departments',    value: stats.departments,            icon: <ApartmentIcon sx={{ fontSize: 18 }} />,         color: '#60a5fa' },
              { label: 'Total Items',    value: stats.total,                  icon: <ExtensionIcon sx={{ fontSize: 18 }} />,         color: '#a5b4fc' },
              { label: 'Granted',        value: stats.granted,                icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,       color: '#4ade80' },
              { label: 'Denied',         value: stats.denied,                 icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} />,    color: '#f87171' },
            ].map((s) => (
              <Paper key={s.label} elevation={0} sx={{
                px: 2, py: 1.25, borderRadius: 1.75,
                border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 160, flex: '1 1 auto',
              }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5,
                  bgcolor: alpha(s.color, 0.18), color: s.color,
                  border: `1px solid ${alpha(s.color, 0.30)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'text.secondary' }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: s.isText ? '0.95rem' : '1.15rem',
                    fontWeight: 800, color: 'text.primary', lineHeight: 1.1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {s.value}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>

          {/* Department accordions */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={42} sx={{ color: PRIMARY }} />
            </Box>
          ) : filteredDepartments.length === 0 ? (
            <Paper elevation={0} sx={{
              borderRadius: 2, border: '1px solid', borderColor: 'divider',
              bgcolor: 'background.paper', textAlign: 'center', py: 6,
            }}>
              <ApartmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                {searchQuery
                  ? `No departments match "${searchQuery}"`
                  : 'No departments accessible for this role.'}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {filteredDepartments.map((department) => {
                const components = departmentComponents[department.id] || [];
                // Apply access filter per-department.
                const visibleComponents = components.filter((c) => {
                  if (accessFilter === 'all') return true;
                  const g = hasAccess(department.id, c.component_name);
                  return accessFilter === 'granted' ? g : !g;
                });
                const grantedCount = components.filter((c) => hasAccess(department.id, c.component_name)).length;
                const isExpanded = expandedDepartments.has(department.id);
                const allGranted = components.length > 0 && grantedCount === components.length;

                return (
                  <Accordion
                    key={department.id}
                    expanded={isExpanded}
                    onChange={() => handleDepartmentToggle(department.id)}
                    disableGutters
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isExpanded ? alpha(PRIMARY, 0.50) : 'divider',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: 'text.secondary' }} />}
                      sx={{
                        px: 2,
                        minHeight: 60,
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1 },
                        '&.Mui-expanded': { bgcolor: alpha(PRIMARY, 0.06) },
                        '&:hover': { bgcolor: alpha(PRIMARY, 0.04) },
                      }}
                    >
                      <Avatar sx={{
                        width: 32, height: 32,
                        bgcolor: alpha(PRIMARY, 0.18), color: PRIMARY,
                        border: `1px solid ${alpha(PRIMARY, 0.30)}`,
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                      }}>
                        <ApartmentIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem', lineHeight: 1.2 }}>
                          {department.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.15 }}>
                          {components.length} component{components.length === 1 ? '' : 's'}
                          {accessFilter !== 'all' && visibleComponents.length !== components.length && (
                            <> · {visibleComponents.length} shown</>
                          )}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label={`${grantedCount}/${components.length} granted`}
                        sx={{
                          height: 22, fontSize: '0.7rem', fontWeight: 700,
                          bgcolor: allGranted ? alpha('#15803d', 0.18) : alpha(PRIMARY, 0.14),
                          color: allGranted ? '#4ade80' : PRIMARY,
                          border: '1px solid',
                          borderColor: allGranted ? alpha('#4ade80', 0.30) : alpha(PRIMARY, 0.30),
                          mr: 1,
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                      {/* Bulk grant/revoke strip */}
                      {components.length > 0 && (
                        <Box sx={{
                          px: 2, py: 1.25,
                          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                          bgcolor: 'action.hover',
                          borderBottom: '1px solid', borderColor: 'divider',
                        }}>
                          <TuneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600 }}>
                            Bulk actions:
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Button
                            size="small"
                            startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleBulkDepartmentAccess(department.id, true)}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                              color: '#4ade80', bgcolor: alpha('#15803d', 0.14),
                              '&:hover': { bgcolor: alpha('#15803d', 0.22) },
                              borderRadius: 1.5, py: 0.4, px: 1.25,
                            }}
                          >
                            Grant All
                          </Button>
                          <Button
                            size="small"
                            startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleBulkDepartmentAccess(department.id, false)}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                              color: '#f87171', bgcolor: alpha('#dc2626', 0.14),
                              '&:hover': { bgcolor: alpha('#dc2626', 0.22) },
                              borderRadius: 1.5, py: 0.4, px: 1.25,
                            }}
                          >
                            Revoke All
                          </Button>
                        </Box>
                      )}

                      <Box sx={{ p: { xs: 1.25, md: 1.75 } }}>
                        {components.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <ExtensionIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                            <Typography variant="body2" color="text.secondary">
                              No components assigned to this department
                            </Typography>
                          </Box>
                        ) : visibleComponents.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No components match the current filter.
                            </Typography>
                          </Box>
                        ) : (
                          <Stack spacing={1}>
                            {visibleComponents.map((component) => {
                              const granted = hasAccess(department.id, component.component_name);
                              return (
                                <Paper
                                  key={component.component_name}
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.75,
                                    border: '1px solid',
                                    borderColor: granted ? alpha('#4ade80', 0.30) : 'divider',
                                    bgcolor: granted ? alpha('#15803d', 0.06) : 'background.paper',
                                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                    transition: 'border-color .15s ease, background-color .15s ease',
                                    '&:hover': {
                                      borderColor: granted ? alpha('#4ade80', 0.50) : alpha(PRIMARY, 0.40),
                                    },
                                  }}
                                >
                                  <Switch
                                    checked={granted}
                                    onChange={(e) => handlePermissionChange(department.id, component.component_name, e.target.checked)}
                                    sx={{ mt: -0.5 }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                      <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>
                                        {component.component_name}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={granted ? 'Granted' : 'Denied'}
                                        sx={{
                                          height: 20, fontSize: '0.66rem', fontWeight: 700,
                                          textTransform: 'uppercase', letterSpacing: '0.4px',
                                          bgcolor: granted ? alpha('#15803d', 0.18) : alpha('#dc2626', 0.14),
                                          color: granted ? '#4ade80' : '#f87171',
                                          border: '1px solid',
                                          borderColor: granted ? alpha('#4ade80', 0.30) : alpha('#f87171', 0.30),
                                          '& .MuiChip-label': { px: 0.85 },
                                        }}
                                      />
                                    </Stack>
                                    {component.description && (
                                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>
                                        {component.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default ComponentAccess;